import { Editor, Element, Transforms, Range, Descendant, Path, BaseSelection } from 'slate';
import { CustomEditor, CustomLink } from '../../../../models/slate-editor';
import { PostEditorTextUpdateAction } from '../../states/PostEditorActions';
import { getSpacesRange } from './getSpacesRange';
import twttr from 'twitter-text';
import { typeGuards } from './typeGuards';
import { BaseLinkSettings } from './onChange';
import { makeFinalLink } from '../../states/reducers/link.reducer';
import { PostEditorLink } from '../../../../models/post-editor';
import { PostLinkData } from '../../../../hooks/usePostLinkSettings';
import { EditorRefType } from '../../states/PostEditorProvider';

export type LinkType = { url: string; text: string };

export const withLinks = (editor: CustomEditor): CustomEditor => {
  let _link: CustomLink | null = null;
  let _postLinkData: PostLinkData | null = null;

  Object.defineProperty(editor, 'link', {
    get: () => _link,
    configurable: false, // Prevents the link property from being redefined
    enumerable: true // Allows the property to be enumerated
  });

  Object.defineProperty(editor, 'postLinkData', {
    get: () => _postLinkData,
    configurable: false, // Prevents the link property from being redefined
    enumerable: true // Allows the property to be enumerated
  });

  Object.defineProperty(editor, 'linksEnabled', {
    get: () => true,
    configurable: false, // Prevents the link property from being redefined
    enumerable: true // Allows the property to be enumerated
  });

  editor.setLink = newLink => {
    _link = newLink;
  };

  editor.setLinkPartial = partial => {
    if (!editor.link) throw new Error('Trying to update partial on null - Link is not defined');
    const newLink = {
      ..._link,
      ...partial
    } as CustomLink;
    editor.setLink(newLink);
  };

  editor.setPostLinkData = value => {
    _postLinkData = value;
  };

  const { isInline, isVoid } = editor;

  editor.isInline = element => {
    return element.type === 'link' ? true : isInline(element);
  };

  editor.isVoid = element => {
    return element.type === 'link' ? true : isVoid(element);
  };

  return editor;
};

export const PostEditorLinkToCustomLink = (link: string, linkSettings: BaseLinkSettings): CustomLink => {
  const postEditorLink = makeFinalLink({
    initialUrl: link,
    ...linkSettings
  });
  return {
    type: 'link',
    children: [
      {
        text: ''
      }
    ],
    url: postEditorLink,
    initialUrl: link,
    ...linkSettings
  };
};

export const hasLinksChecker = (node: Descendant[]): CustomLink | false =>
  node.reduce((prev: false | CustomLink, elem) => {
    if (prev) return prev;
    if (Element.isElement(elem) && elem.type === 'link') {
      return elem;
    }
    if (Element.isElement(elem) && elem.type !== 'paragraph') {
      return prev;
    }
    if (Element.isElement(elem) && elem.type === 'paragraph' && elem.children.length) {
      return hasLinksChecker(elem.children);
    }
    return prev;
  }, false);

export const removeLink = (editor: CustomEditor): void => {
  Transforms.removeNodes(editor, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    at: []
  });
  editor.setLink(null);
};

export const createLinkNode = (link: PostEditorLink): CustomLink => ({
  type: 'link',
  ...link,
  children: [{ text: '' }]
});

const checkIfSelectionIncludesPath = (selection: BaseSelection, path: Path) => {
  // Ensure there's an active selection and it's a Range
  if (selection && Range.isRange(selection)) {
    // Check if the selection includes the specified path
    return Range.includes(selection, path);
  }

  return false;
};

export const insertLink = (
  editor: CustomEditor,
  target: Range,
  payload: PostEditorTextUpdateAction['payload']
): PostEditorTextUpdateAction['payload'] => {
  if (!payload.link) throw new Error('Link is not provided');
  const link = createLinkNode(payload.link);
  const { selection: initialSelection } = editor;

  const at = getSpacesRange(editor, target);
  Transforms.delete(editor, { at });
  Transforms.insertNodes(editor, link, { at: at.anchor, select: true });
  const { selection: currentSelection } = editor;
  const linkPath = currentSelection?.anchor.path ? Path.parent(currentSelection.anchor.path) : null; // this will return the link paths itself, since we can only select texts (inside this inline node)
  const prevPath = linkPath ? Path.previous(linkPath) : null;
  const nextPath = linkPath ? Path.next(linkPath) : null;
  const prevText = prevPath && Editor.string(editor, prevPath);
  const nextText = nextPath && Editor.string(editor, nextPath);
  const isAtLink = Array.from(
    Editor.nodes(editor, {
      match: (node, path) =>
        Element.isElement(node) && node.type === 'link' && checkIfSelectionIncludesPath(currentSelection, path)
    })
  ).length;
  if (prevText) {
    Transforms.insertText(editor, ' ', { at: { path: prevPath, offset: prevText.length } });
  }
  if (nextText) {
    Transforms.insertText(editor, ' ', { at: { path: nextPath, offset: 0 } });
  }
  if (initialSelection && currentSelection && isAtLink && nextPath) {
    Transforms.select(editor, { path: nextPath, offset: 0 });
    // Transforms.move(editor, { unit: 'character' });
    // we are inserting the link where we are now, so make sure we set selection after the link
  }
  const fragment = Editor.fragment(editor, []);
  if (!typeGuards.isCustomParagraphArrayGuard(fragment))
    throw new Error('provided fragment is not of type CustomParagpraph.');
  // const { selection: currentAfterInsert } = editor;
  // const elements = Node.elements;
  if (currentSelection?.anchor.path) payload.text = fragment;
  return payload;
};

export const linkMatcher = (inputText: string): LinkType | null => {
  try {
    const result = twttr.extractUrls(inputText);
    if (!result.length) {
      return null;
    } else {
      return {
        url: result[0],
        text: inputText
      };
    }
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const allLinksInText = (inputText: string): LinkType[] => {
  try {
    const result = twttr.extractUrls(inputText);
    return result.map(url => ({ url, text: inputText }));
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const updateLink = (editorRefs: EditorRefType[], editor: CustomEditor, link: PostEditorLink): void => {
  const matched = linkMatcher(link.initialUrl);
  if (!matched) return;
  const result = createLinkNode(link);
  editorRefs.forEach(({ editor }) => {
    editor.setLink(result);
  });
  Transforms.setNodes<CustomLink>(editor, result, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    mode: 'all',
    at: []
  });
};

export const updateLinkPartial = (editor: CustomEditor, linkPartial: Partial<PostEditorLink>): void => {
  if (typeof linkPartial.initialUrl === 'string') {
    editor.setLinkPartial(linkPartial);
  }
  Transforms.setNodes<CustomLink>(
    editor,
    { ...linkPartial },
    {
      match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
      mode: 'all',
      at: []
    }
  );
};
